"use client";

import { type FC, useEffect } from "react";
import { MotionConfig } from "motion/react";
import { isWindowDefined, mergeClasses } from "@huub-web/utils";

import type { RequiredChildrenProp } from "../../props/ChildrenProp";
import type { ThemeName } from "../../theme/types";

import { appStyle, portalStyle } from "./App.css";

import "../../theme/css/fonts.css";
import "../../theme/css/globalReset.css";
import "../../theme/css/globalTypography.css";
import { ThemeContext, type ThemeContextState } from "../../contexts";

export type AppProps = RequiredChildrenProp & {
  /**
   * Optional className to apply to the root element
   */
  className?: string;
  theme?: ThemeName;
  themeClasses: ThemeContextState["themeClasses"];
};

/**
 * The main component of the application.
 */
export const App: FC<AppProps> = ({
  className,
  theme = "light",
  themeClasses,
  children,
}) => {
  useEffect(() => {
    if (!themeClasses[theme] || !isWindowDefined()) {
      throw new Error(`Theme ${theme} not found in themeClasses`);
    }
    document.body.classList.remove(...Object.values(themeClasses));
    document.body.classList.add(themeClasses[theme]);
  }, [theme, themeClasses]);

  return (
    <MotionConfig reducedMotion="user">
      <ThemeContext.Provider value={{ theme, themeClasses }}>
        <div
          className={mergeClasses(themeClasses?.[theme], appStyle, className)}
        >
          {children}
          <div id="portal" className={portalStyle} />
        </div>
      </ThemeContext.Provider>
    </MotionConfig>
  );
};
